import { Component } from 'preact';
import {
  detectAtQuery,
  filterCards,
  serializeCardMention,
  parseCardMentions,
  rectToCanvasRect,
} from '../utils/cardMentions';
import CardMentionPopover, { choosePopoverSide } from './CardMentionPopover';

// Atomic non-editable card pill. Identical structure to the one
// MarkdownEditor builds, so the shared .md-card-link CSS applies
// without needing a chat-specific class. The pill's full source
// (`[Name](card:id)`) lives on data-card-source so getPlainText can
// round-trip the canonical token back out of the DOM.
function buildPill(name, cardId, handlers) {
  const span = document.createElement('span');
  span.className = 'md-card-link';
  span.contentEditable = 'false';
  span.dataset.cardId = cardId;
  span.dataset.cardSource = `[${name}](card:${cardId})`;
  span.textContent = name;
  if (handlers?.onCardClick) {
    span.addEventListener('click', (e) => handlers.onCardClick(cardId, e));
  }
  if (handlers?.onCardHover) {
    span.addEventListener('mouseenter', () => handlers.onCardHover(cardId, span));
  }
  if (handlers?.onCardHoverEnd) {
    span.addEventListener('mouseleave', () => handlers.onCardHoverEnd());
  }
  return span;
}

function nodeSourceLength(node) {
  if (!node) return 0;
  if (node.nodeType === Node.TEXT_NODE) return (node.nodeValue || '').length;
  if (node.nodeType !== Node.ELEMENT_NODE) return 0;
  if (node.dataset?.cardSource) return node.dataset.cardSource.length;
  let total = 0;
  for (const child of node.childNodes) total += nodeSourceLength(child);
  return total;
}

// Root children grouped into visual lines. _renderContent emits one div
// per line, but Chromium drops that div when Backspace is pressed in an
// empty editor (or after select-all + delete), leaving a bare <br> or
// text node at the root. Any run of non-div root nodes is one line, so
// getPlainText and getCaretOffset agree with each other and with what
// the user sees.
function groupRootLines(children) {
  const lines = [];
  let run = null;
  for (const child of children) {
    if (child.nodeName === 'DIV') {
      run = null;
      lines.push([child]);
    } else {
      if (!run) { run = []; lines.push(run); }
      run.push(child);
    }
  }
  return lines;
}

// Chat-focused contenteditable that renders text + card-mention pills
// inline. Same source format as deck notes (`[Name](card:id)` joined
// with `\n` for line breaks), same autocomplete popover, but no
// markdown formatting (no bold / italic / lists / headings) and Enter
// is configurable per surface. Friend-chat sends, in-game logbook
// sends, deck notes still inserts a newline.
//
// Props:
//   value, onValueChange. Controlled source text
//   sorceryCards. Required for @-autocomplete
//   onSubmit. When given, Enter calls this (Shift+Enter
//                           still inserts a newline in multi-line mode)
//   singleLine. Disables Enter/Shift+Enter inserting
//                           newlines and strips them from paste
//   maxLength. Caps the source-text length
//   placeholder. Empty-state hint shown via CSS
//   onOpenCard. (cardId, event) => void; click handler on
//                           the rendered pill (open inspector)
//   onCardHover/HoverEnd. Hover preview hooks (same shape as
//                           MarkdownEditor's card-link hover)
//   inputRef. Forwards the editor div to the parent
//                           (focus/blur, scroll-into-view, etc.)
export default class CardMentionEditor extends Component {
  constructor(props) {
    super(props);
    this.editorRef = null;
    this._lastValue = props.value || '';
    this.state = { autocomplete: null };
  }

  componentDidMount() {
    this._renderContent(this._lastValue);
    document.addEventListener('mousedown', this._handleDocMouseDown, true);
  }

  componentDidUpdate(prevProps) {
    if (this.props.value !== prevProps.value && this.props.value !== this._lastValue) {
      this._lastValue = this.props.value || '';
      this._renderContent(this._lastValue);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this._handleDocMouseDown, true);
  }

  _forwardRef = (el) => {
    this.editorRef = el;
    const { inputRef } = this.props;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef && typeof inputRef === 'object') inputRef.current = el;
  };

  _handleDocMouseDown = (e) => {
    if (!this.state.autocomplete) return;
    if (this.editorRef && this.editorRef.contains(e.target)) return;
    const popoverEl = document.querySelector('.md-autocomplete');
    if (popoverEl && popoverEl.contains(e.target)) return;
    this.setState({ autocomplete: null });
  };

  _renderContent(text) {
    const editor = this.editorRef;
    if (!editor) return;
    while (editor.firstChild) editor.removeChild(editor.firstChild);

    const handlers = {
      onCardClick: (cardId, e) => this.props.onOpenCard?.(cardId, e),
      onCardHover: (cardId, el) => this.props.onCardHover?.(cardId, el),
      onCardHoverEnd: () => this.props.onCardHoverEnd?.(),
    };

    if (this.props.singleLine) {
      this._appendInline(editor, text, handlers);
    } else {
      const lines = text.split('\n');
      for (const line of lines) {
        const lineDiv = document.createElement('div');
        lineDiv.className = 'cme-line';
        this._appendInline(lineDiv, line, handlers);
        if (lineDiv.childNodes.length === 0) {
          lineDiv.appendChild(document.createElement('br'));
        }
        editor.appendChild(lineDiv);
      }
    }
  }

  _appendInline(parent, text, handlers) {
    for (const chunk of parseCardMentions(text)) {
      if (chunk.type === 'text') {
        if (chunk.value) parent.appendChild(document.createTextNode(chunk.value));
      } else {
        parent.appendChild(buildPill(chunk.name, chunk.cardId, handlers));
      }
    }
  }

  getPlainText() {
    const editor = this.editorRef;
    if (!editor) return '';
    if (this.props.singleLine) {
      return this._collectInline(editor);
    }
    return this._sourceOfRootNodes(editor.childNodes);
  }

  _sourceOfRootNodes(children) {
    return groupRootLines(children)
      .map((nodes) => nodes.map((n) => this._collectInline(n)).join(''))
      .join('\n');
  }

  _hasLineStructure() {
    const children = this.editorRef.childNodes;
    return children.length > 0 && Array.from(children).every((n) => n.nodeName === 'DIV');
  }

  _collectInline(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    if (node.dataset?.cardSource) return node.dataset.cardSource;
    let out = '';
    for (const child of node.childNodes) {
      if (child.nodeName === 'BR') {
        // Don't emit a \n for the placeholder <br> in an empty line.
        // the line div itself contributes its own separator. <br>s
        // injected mid-line by Chromium during edits become \n.
        if (child === node.firstChild && child === node.lastChild) continue;
        out += '\n';
        continue;
      }
      out += this._collectInline(child);
    }
    return out;
  }

  getCaretOffset() {
    if (!this.editorRef) return 0;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 0;
    const range = sel.getRangeAt(0);
    if (!this.editorRef.contains(range.endContainer)) return 0;

    const { endContainer: container, endOffset } = range;
    if (this.props.singleLine) {
      return this._offsetWithin(this.editorRef, container, endOffset);
    }
    if (container === this.editorRef) {
      // Caret parked between root children (Chromium does this after
      // unwrapping the line div): everything before it is the offset.
      return this._sourceOfRootNodes(Array.from(this.editorRef.childNodes).slice(0, endOffset)).length;
    }
    let offset = 0;
    for (const nodes of groupRootLines(this.editorRef.childNodes)) {
      if (nodes.some((n) => n === container || (n.nodeType === Node.ELEMENT_NODE && n.contains(container)))) {
        return offset + this._offsetWithinNodes(nodes, container, endOffset);
      }
      offset += nodes.reduce((len, n) => len + nodeSourceLength(n), 0) + 1;
    }
    return Math.max(0, offset - 1);
  }

  _offsetWithin(parent, container, containerOffset) {
    if (parent === container) {
      let count = 0;
      for (let i = 0; i < containerOffset; i++) {
        count += nodeSourceLength(parent.childNodes[i]);
      }
      return count;
    }
    return this._offsetWithinNodes(parent.childNodes, container, containerOffset);
  }

  _offsetWithinNodes(nodes, container, containerOffset) {
    let count = 0;
    for (const child of nodes) {
      if (child === container) {
        if (child.nodeType === Node.TEXT_NODE) return count + containerOffset;
        if (child.dataset?.cardSource) return count + child.dataset.cardSource.length;
        return count + this._offsetWithin(child, container, containerOffset);
      }
      if (child.nodeType === Node.ELEMENT_NODE && child.contains(container)) {
        if (child.dataset?.cardSource) return count + child.dataset.cardSource.length;
        return count + this._offsetWithin(child, container, containerOffset);
      }
      count += nodeSourceLength(child);
    }
    return count;
  }

  setCaretOffset(target) {
    if (!this.editorRef) return;
    let remaining = Math.max(0, target);
    if (this.props.singleLine) {
      this._placeCaretWithin(this.editorRef, remaining);
      return;
    }
    for (const lineDiv of this.editorRef.childNodes) {
      if (lineDiv.nodeType !== Node.ELEMENT_NODE) continue;
      const lineLen = nodeSourceLength(lineDiv);
      if (remaining <= lineLen) {
        this._placeCaretWithin(lineDiv, remaining);
        return;
      }
      remaining -= lineLen + 1;
    }
    this._placeCaretAtEnd();
  }

  _placeCaretWithin(parent, sourceOffset) {
    const sel = window.getSelection();
    const range = document.createRange();
    let remaining = sourceOffset;
    for (let i = 0; i < parent.childNodes.length; i++) {
      const child = parent.childNodes[i];
      const len = nodeSourceLength(child);
      if (remaining <= len) {
        if (child.nodeType === Node.TEXT_NODE) {
          range.setStart(child, remaining);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        if (child.nodeType === Node.ELEMENT_NODE && child.dataset?.cardSource) {
          if (remaining === 0) range.setStartBefore(child);
          else range.setStartAfter(child);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          return;
        }
        if (child.nodeType === Node.ELEMENT_NODE) {
          this._placeCaretWithin(child, remaining);
          return;
        }
      }
      remaining -= len;
    }
    range.selectNodeContents(parent);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }

  _placeCaretAtEnd() {
    if (!this.editorRef) return;
    const range = document.createRange();
    range.selectNodeContents(this.editorRef);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  }

  focus() {
    if (this.editorRef) {
      this.editorRef.focus();
      this._placeCaretAtEnd();
    }
  }

  _syncFromDom = () => {
    let text = this.getPlainText();
    const { maxLength } = this.props;
    let truncated = false;
    if (typeof maxLength === 'number' && text.length > maxLength) {
      text = text.slice(0, maxLength);
      truncated = true;
    }
    const offset = truncated ? text.length : this.getCaretOffset();
    if (text === this._lastValue && !truncated) {
      // Backspace in an empty editor leaves the root without a line
      // div (see groupRootLines). Rebuild so the next keystroke lands
      // inside a line rather than as a bare root text node.
      if (!this.props.singleLine && !this._hasLineStructure()) {
        this._renderContent(text);
        this.setCaretOffset(offset);
      }
      this._updateAutocomplete();
      return;
    }
    this._lastValue = text;
    this._renderContent(text);
    this.setCaretOffset(offset);
    if (this.props.onValueChange) this.props.onValueChange(text);
    this._updateAutocomplete();
  };

  _updateAutocomplete = () => {
    const { sorceryCards } = this.props;
    if (!sorceryCards) {
      if (this.state.autocomplete) this.setState({ autocomplete: null });
      return;
    }
    const text = this._lastValue;
    const offset = this.getCaretOffset();
    const match = detectAtQuery(text, offset);
    if (!match) {
      if (this.state.autocomplete) this.setState({ autocomplete: null });
      return;
    }
    const results = filterCards(sorceryCards, match.query);
    if (results.length === 0) {
      if (this.state.autocomplete) this.setState({ autocomplete: null });
      return;
    }
    // Anchor to the editor element's own rect, not the caret pixel.
    // chat composers sit in a fixed band, so opening above the band
    // feels more intentional than chasing the caret line by line.
    const anchor = rectToCanvasRect(this.editorRef.getBoundingClientRect());
    const prev = this.state.autocomplete;
    const placeAbove = prev && prev.atIndex === match.atIndex
      ? prev.placeAbove
      : choosePopoverSide(anchor, results.length);
    this.setState({
      autocomplete: {
        atIndex: match.atIndex,
        query: match.query,
        results,
        selected: 0,
        anchor,
        placeAbove,
      },
    });
  };

  _handleInput = () => {
    this._syncFromDom();
  };

  _handleKeyDown = (e) => {
    const { autocomplete } = this.state;
    if (autocomplete) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.setState({
          autocomplete: {
            ...autocomplete,
            selected: (autocomplete.selected + 1) % autocomplete.results.length,
          },
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setState({
          autocomplete: {
            ...autocomplete,
            selected:
              (autocomplete.selected - 1 + autocomplete.results.length)
              % autocomplete.results.length,
          },
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        this._insertCard(autocomplete.results[autocomplete.selected]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        this.setState({ autocomplete: null });
        return;
      }
    }
    if (e.key === 'Enter') {
      const wantsSubmit = !!this.props.onSubmit && !e.shiftKey;
      if (wantsSubmit || this.props.singleLine) {
        e.preventDefault();
        if (this.props.onSubmit) this.props.onSubmit();
        return;
      }
    }
    if (this.props.onKeyDown) this.props.onKeyDown(e);
  };

  _insertCard = (card) => {
    const { autocomplete } = this.state;
    if (!autocomplete || !card) return;
    const text = this._lastValue;
    const queryEnd = this.getCaretOffset();
    const linkText = `${serializeCardMention(card)} `;
    const next = text.slice(0, autocomplete.atIndex) + linkText + text.slice(queryEnd);
    const caretPos = autocomplete.atIndex + linkText.length;
    this.setState({ autocomplete: null });
    this._lastValue = next;
    this._renderContent(next);
    this.setCaretOffset(caretPos);
    if (this.props.onValueChange) this.props.onValueChange(next);
  };

  _handlePaste = (e) => {
    e.preventDefault();
    let text = e.clipboardData?.getData('text/plain') || '';
    if (this.props.singleLine) text = text.replace(/[\r\n]+/g, ' ');
    if (!text) return;
    // Splice the paste into the source text at the current selection.
    const range = window.getSelection()?.rangeCount
      ? window.getSelection().getRangeAt(0)
      : null;
    if (!range) return;
    const start = this.getCaretOffset();
    if (!range.collapsed) {
      // Move the start to the selection's start point, drop the
      // selected source from the value.
      const endOffset = this.getCaretOffset();
      range.collapse(true);
      const startOffset = this.getCaretOffset();
      const before = this._lastValue.slice(0, Math.min(startOffset, endOffset));
      const after = this._lastValue.slice(Math.max(startOffset, endOffset));
      const merged = before + text + after;
      const capped = typeof this.props.maxLength === 'number'
        ? merged.slice(0, this.props.maxLength)
        : merged;
      this._lastValue = capped;
      this._renderContent(capped);
      this.setCaretOffset(Math.min(startOffset, endOffset) + (capped.length - before.length - after.length));
      if (this.props.onValueChange) this.props.onValueChange(capped);
      this._updateAutocomplete();
      return;
    }
    const before = this._lastValue.slice(0, start);
    const after = this._lastValue.slice(start);
    const merged = before + text + after;
    const capped = typeof this.props.maxLength === 'number'
      ? merged.slice(0, this.props.maxLength)
      : merged;
    this._lastValue = capped;
    this._renderContent(capped);
    this.setCaretOffset(Math.min(start + text.length, capped.length));
    if (this.props.onValueChange) this.props.onValueChange(capped);
    this._updateAutocomplete();
  };

  _handleSelect = (card) => this._insertCard(card);

  _handleHover = (idx) => {
    const { autocomplete } = this.state;
    if (!autocomplete) return;
    this.setState({ autocomplete: { ...autocomplete, selected: idx } });
  };

  render() {
    const {
      className = '',
      style,
      placeholder,
      singleLine,
      onValueChange: _v,
      sorceryCards: _s,
      onSubmit: _sub,
      onKeyDown: _k,
      onOpenCard: _o,
      onCardHover: _h,
      onCardHoverEnd: _he,
      inputRef: _r,
      maxLength: _m,
      value: _val,
      onBlur,
      onFocus,
      ...rest
    } = this.props;

    const isEmpty = !(this._lastValue || '').length;
    return (
      <>
        <div
          {...rest}
          ref={this._forwardRef}
          className={`card-mention-editor ${singleLine ? 'is-single-line' : ''} ${isEmpty ? 'is-empty' : ''} ${className}`}
          style={style}
          contentEditable
          suppressContentEditableWarning
          spellcheck
          data-placeholder={placeholder || ''}
          onInput={this._handleInput}
          onKeyDown={this._handleKeyDown}
          onKeyUp={this._updateAutocomplete}
          onClick={this._updateAutocomplete}
          onPaste={this._handlePaste}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        {this.state.autocomplete ? (
          <CardMentionPopover
            results={this.state.autocomplete.results}
            selected={this.state.autocomplete.selected}
            anchor={this.state.autocomplete.anchor}
            placeAbove={this.state.autocomplete.placeAbove}
            onHover={this._handleHover}
            onSelect={this._handleSelect}
          />
        ) : null}
      </>
    );
  }
}
